<script setup>
import { computed, ref } from "vue";

const props = defineProps({
  volumes: { type: Array, default: () => [] },
  sentiments: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  tipLabels: { type: Array, default: () => [] },
  width: { type: Number, default: 720 },
  height: { type: Number, default: 280 },
});

const hovered = ref(null);
const containerRef = ref(null);

const pad = { l: 48, r: 48, t: 20, b: 30 };

function smooth(pts) {
  if (pts.length < 2) return "";
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const cx = (pts[i][0] + pts[i + 1][0]) / 2;
    d += ` C${cx.toFixed(1)} ${pts[i][1].toFixed(1)}, ${cx.toFixed(1)} ${pts[i + 1][1].toFixed(1)}, ${pts[i + 1][0].toFixed(1)} ${pts[i + 1][1].toFixed(1)}`;
  }
  return d;
}

const chart = computed(() => {
  const volumes = props.volumes.map(Number).filter(Number.isFinite);
  const sentiments = props.sentiments.map(Number).filter(Number.isFinite);
  const n = Math.min(volumes.length, sentiments.length);
  if (n < 2) return null;
  const iw = props.width - pad.l - pad.r;
  const ih = props.height - pad.t - pad.b;
  const maxVolume = Math.max(...volumes.slice(0, n), 1) * 1.15;
  const slot = iw / (n - 1 || 1);
  const barWidth = Math.max(4, Math.min(22, slot * 0.56));
  const x = (index) => pad.l + index * slot;
  const yVolume = (value) => pad.t + ih - (value / maxVolume) * ih;
  const ySentiment = (value) => pad.t + ih - ((value + 100) / 200) * ih;

  const points = sentiments.slice(0, n).map((value, index) => ({
    value,
    x: x(index),
    y: ySentiment(value),
    volume: volumes[index],
    label: props.labels[index],
    tipLabel: props.tipLabels[index] ?? props.labels[index],
  }));

  const lineSegments = [];
  let segStart = 0;
  for (let i = 1; i < points.length; i++) {
    const prevNeg = points[i - 1].value < 0;
    if (prevNeg !== points[i].value < 0) {
      const pts = points.slice(segStart, i + 1);
      lineSegments.push({
        d: smooth(pts.map((p) => [p.x, p.y])),
        neg: points[segStart].value < 0,
      });
      segStart = i;
    }
  }
  const tail = points.slice(segStart);
  lineSegments.push({
    d: smooth(tail.map((p) => [p.x, p.y])),
    neg: points[segStart].value < 0,
  });

  const fullLine = points.map((p) => [p.x, p.y]);
  const areaD =
    smooth(fullLine) +
    `L${points[n - 1].x.toFixed(1)} ${pad.t + ih}` +
    `L${points[0].x.toFixed(1)} ${pad.t + ih}Z`;

  const sentimentLabels = [-100, -50, 0, 50, 100].map((v) => ({
    label: v,
    y: ySentiment(v),
  }));
  const labelEvery = Math.ceil(n / 7);

  return {
    points,
    lineSegments,
    areaD,
    volumes: volumes.slice(0, n),
    n,
    ih,
    slot,
    barWidth,
    iw,
    pad,
    x,
    yVolume,
    ySentiment,
    yTicks: sentimentLabels,
    labels: props.labels
      .slice(0, n)
      .map((label, i) => ({ label, x: x(i) }))
      .filter((_, i) => i % labelEvery === 0),
  };
});

function onMouseMove(e) {
  const svg = containerRef.value?.querySelector("svg");
  if (!svg || !chart.value) return;
  const rect = svg.getBoundingClientRect();
  const mx = (e.clientX - rect.left) * (props.width / rect.width);
  const idx = Math.round((mx - pad.l) / chart.value.slot);
  hovered.value = idx >= 0 && idx < chart.value.n ? idx : null;
}

function onMouseLeave() {
  hovered.value = null;
}
</script>

<template>
  <div
    ref="containerRef"
    class="bt-trend-wrap"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <svg
      class="h-auto w-full"
      :viewBox="`0 0 ${width} ${height}`"
      fill="none"
      aria-label="Review volume and sentiment trend"
    >
      <defs>
        <linearGradient id="bt-volg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="var(--brand)" stop-opacity="0.9" />
          <stop offset="1" stop-color="var(--brand)" stop-opacity="0.35" />
        </linearGradient>
        <linearGradient id="bt-posa" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="var(--pos)" stop-opacity="0.18" />
          <stop offset="1" stop-color="var(--pos)" stop-opacity="0" />
        </linearGradient>
        <linearGradient id="bt-nega" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="var(--neg)" stop-opacity="0.18" />
          <stop offset="1" stop-color="var(--neg)" stop-opacity="0" />
        </linearGradient>
      </defs>
      <template v-if="chart">
        <line
          v-for="g in [0, 0.5, 1]"
          :key="g"
          :x1="pad.l"
          :x2="width - pad.r"
          :y1="pad.t + chart.ih * g"
          :y2="pad.t + chart.ih * g"
          stroke="var(--border)"
          stroke-width="1"
          :stroke-dasharray="g === 1 ? '0' : '3 4'"
        />
        <line
          :x1="pad.l"
          :x2="width - pad.r"
          :y1="chart.ySentiment(0)"
          :y2="chart.ySentiment(0)"
          stroke="var(--border-2)"
          stroke-width="1"
          stroke-dasharray="2 3"
        />
        <text
          v-for="(tick, i) in chart.yTicks"
          :key="`yt${i}`"
          :x="pad.l - 8"
          :y="tick.y + 3.5"
          text-anchor="end"
          font-size="9.5"
          font-family="var(--mono)"
          fill="var(--text-3)"
        >
          {{ tick.label }}
        </text>
        <text
          :x="width - pad.r + 10"
          :y="pad.t + chart.ih * 0.18 + 4"
          text-anchor="start"
          font-size="9"
          font-family="var(--mono)"
          fill="var(--text-3)"
        >
          Sent.
        </text>
        <text
          :x="width - pad.r + 10"
          :y="pad.t + chart.ih * 0.82 + 4"
          text-anchor="start"
          font-size="9"
          font-family="var(--mono)"
          fill="var(--text-3)"
        >
          Vol.
        </text>
        <rect
          v-for="(value, index) in chart.volumes"
          :key="index"
          :x="chart.x(index) - chart.barWidth / 2"
          :y="chart.yVolume(value)"
          :width="chart.barWidth"
          :height="pad.t + chart.ih - chart.yVolume(value)"
          :rx="Math.min(3, chart.barWidth / 2)"
          :opacity="hovered === null || hovered === index ? 1 : 0.35"
          fill="url(#bt-volg)"
        />
        <path :d="chart.areaD" fill="url(#bt-posa)" />
        <path
          v-for="(seg, i) in chart.lineSegments"
          :key="`ln${i}`"
          :d="seg.d"
          fill="none"
          :stroke="seg.neg ? 'var(--neg)' : 'var(--pos)'"
          stroke-width="2.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <circle
          v-for="(point, index) in chart.points"
          :key="`m${index}`"
          :cx="point.x"
          :cy="point.y"
          :r="hovered === index ? 4 : 2.8"
          :fill="point.value < 0 ? 'var(--neg)' : 'var(--pos)'"
          :stroke="
            hovered === index
              ? point.value < 0
                ? 'var(--neg)'
                : 'var(--pos)'
              : 'var(--surface)'
          "
          :stroke-width="hovered === index ? 0 : 2"
          style="
            transition:
              r 0.1s,
              stroke-width 0.1s;
          "
        />
        <text
          v-for="(tick, index) in chart.labels"
          :key="`l${index}`"
          :x="tick.x"
          :y="height - 5"
          text-anchor="middle"
          font-size="10"
          font-family="var(--mono)"
          fill="var(--text-3)"
        >
          {{ tick.label }}
        </text>
        <rect
          v-for="index in chart.n"
          :key="`ht${index}`"
          :x="chart.x(index - 1) - chart.slot / 2"
          y="0"
          :width="chart.slot"
          :height="height"
          fill="transparent"
          style="cursor: crosshair"
        />
      </template>
      <text
        v-else
        :x="width / 2"
        :y="height / 2"
        text-anchor="middle"
        fill="var(--text-3)"
        font-size="13"
      >
        No trend data available
      </text>
    </svg>
    <div
      v-if="hovered !== null && chart"
      class="bt-trend-tip"
      :style="{
        left: chart.points[hovered].x + 'px',
        top: Math.min(chart.points[hovered].y - 10, height - 80) + 'px',
      }"
    >
      <div class="bt-tt-label">{{ chart.points[hovered].tipLabel }}</div>
      <div class="bt-tt-row">
        <span class="bt-tt-dot" :style="{ background: 'var(--pos)' }" />
        Sentiment
        <b :class="chart.points[hovered].value < 0 ? 'neg' : ''">{{
          chart.points[hovered].value
        }}</b>
      </div>
      <div class="bt-tt-row">
        <span class="bt-tt-dot" :style="{ background: 'var(--brand)' }" />
        Volume <b>{{ chart.points[hovered].volume }}</b>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bt-trend-wrap {
  position: relative;
  width: 100%;
}
.bt-trend-tip {
  position: absolute;
  transform: translate(-50%, -100%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 7px 11px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.5;
}
.bt-tt-label {
  font-size: 10.5px;
  color: var(--text-3);
  margin-bottom: 3px;
}
.bt-tt-row {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--text-2);
}
.bt-tt-row b.neg {
  color: var(--neg);
}
.bt-tt-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
